import type {
    AccesoDeInicio,
    AccesoEnElMenu,
    AnidamientoNavegacion,
    Aviso,
    Bloques,
    Contador,
    EntradaNavegacion,
    EstadoSuscripcion,
    GrupoNavegacion,
    ListaFacturas,
    ListaInvitaciones,
    ListaMiembros,
    ListaRoles,
    ListaTablets,
    Navegacion,
    Negocio,
    PermisoEfectivo,
    PreferenciasMenu,
    Suscripcion,
    Yo,
} from '../api/operaciones';

/**
 * Datos simulados del Paso 0, con los tipos generados del contrato: si el contrato cambia, esto deja de compilar.
 * Son los del prototipo del equipo (diseno/plataforma/0.1, 0.5, 1.3 y 1.4): Uri Ramírez en tres negocios, Caseta
 * Embrujo (activa, Propietario), Sala Andén (en prueba, Administrador) y Peluquería Lola (impagada, Propietario).
 * Se borran cuando exista el módulo Identidad y acceso.
 */
export const NEGOCIOS = {
    embrujo: 'biz_01JA8Z6Q5V3K9R2M7T4N8P1CE0',
    anden: 'biz_01JA8Z6Q5V3K9R2M7T4N8P1SA0',
    lola: 'biz_01JA8Z6Q5V3K9R2M7T4N8P1PL0',
} as const;

/** Estado de la suscripción de cada negocio en el prototipo. */
const ESTADO_INICIAL: Record<string, EstadoSuscripcion> = {
    [NEGOCIOS.embrujo]: 'active',
    [NEGOCIOS.anden]: 'trialing',
    [NEGOCIOS.lola]: 'past_due',
};

/** Estados en los que el negocio queda en solo lectura (ficha del núcleo §4; G13). */
const SOLO_LECTURA: readonly EstadoSuscripcion[] = ['trial_expired', 'suspended', 'paused', 'cancelled', 'closed'];

export function modoDe(estado: EstadoSuscripcion): 'operational' | 'read_only' {
    return SOLO_LECTURA.includes(estado) ? 'read_only' : 'operational';
}

/**
 * Estado de la suscripción de un negocio en la simulación: el del prototipo o el forzado (`?suscripcion=` en el negocio
 * en el que se entra, `?simular=solo-lectura` = todos en pausa de temporada).
 */
export function estadoDe(negocio: string, opciones: { soloLectura?: boolean; suscripcion?: EstadoSuscripcion | null; negocioForzado?: string | null } = {}): EstadoSuscripcion {
    if (opciones.soloLectura === true) {
        return 'paused';
    }
    if (opciones.suscripcion != null && (opciones.negocioForzado == null || opciones.negocioForzado === negocio)) {
        return opciones.suscripcion;
    }
    return ESTADO_INICIAL[negocio] ?? 'active';
}

export const CUENTA_DEMO = { email: 'uri@reservaria.app', contrasenaErronea: 'error', codigoErroneo: '000000' } as const;

export function yo(unNegocio: boolean, estado: (negocio: string) => EstadoSuscripcion = (n) => estadoDe(n), sinNegocio = false): Yo {
    const membresia = (business_id: string, business_name: string, slug: string, member: string, is_owner: boolean, rol: { id: string; code: string; name: string }) => {
        const subscription_status = estado(business_id);
        return {
            business_id,
            business_name,
            slug,
            public_url: `https://reservaria.app/${slug}`,
            member_id: member,
            is_owner,
            roles: [rol],
            subscription_mode: modoDe(subscription_status),
            subscription_status,
        };
    };
    const propietario = { id: 'rol_propietario', code: 'propietario', name: 'Propietario' };
    const memberships: Yo['memberships'] = [
        membresia(NEGOCIOS.embrujo, 'Caseta Embrujo', 'embrujo', 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M01', true, propietario),
        membresia(NEGOCIOS.anden, 'Sala Andén', 'sala-anden', 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M02', false, { id: 'rol_administrador', code: 'administrador', name: 'Administrador' }),
        membresia(NEGOCIOS.lola, 'Peluquería Lola', 'peluqueria-lola', 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M03', true, propietario),
    ];
    return {
        account: {
            id: 'acc_01JA8Z6Q5V3K9R2M7T4N8P1R0M',
            email: CUENTA_DEMO.email,
            email_verified: true,
            display_name: 'Uri Ramírez',
            locale: 'es-ES',
            type: 'staff',
            platform_role: null,
            two_factor_enabled: true,
        },
        memberships: sinNegocio ? [] : unNegocio ? memberships.slice(0, 1) : memberships,
    };
}

interface EntradaSimulada {
    item: EntradaNavegacion;
    /** Permiso que la muestra (null: cualquier cuenta con sesión; varios: basta uno, G628 y G684) y bloque que la activa, como en los manifiestos. */
    permiso: string | string[] | null;
    bloque: string | null;
}

const menu = (module: string, key: string, label: string, group: GrupoNavegacion, icon: string, order: number, permiso: string | string[], bloque: string | null, badge: number | null = null): EntradaSimulada => ({
    item: { module, key, label, path: `/${key}`, area: 'menu', group, icon, order, badge, in_more: false, not_contracted: false },
    permiso,
    bloque,
});
const dentro = (module: string, key: string, padre: string, nested_as: AnidamientoNavegacion, label: string, icon: string, order: number, permiso: string, bloque: string | null): EntradaSimulada => ({
    item: { module, key, label, path: `/${padre}/${key}`, area: 'menu', group: 'settings', icon, order, parent_key: padre, nested_as, in_more: false, not_contracted: false },
    permiso,
    bloque,
});
const perfil = (module: string, key: string, path: string, label: string, icon: string, order: number, permiso: string | null): EntradaSimulada => ({
    item: { module, key, label, path, area: 'profile', group: null, icon, order, in_more: false, not_contracted: false },
    permiso,
    bloque: null,
});

/**
 * Entradas que declaran los manifiestos, por las seis categorías de G128, como el prototipo (0.1, captura
 * `0.1--normal--escritorio`). Las de las fichas llevan sus datos; las que el prototipo enseña y aún no tienen fila en su
 * ficha (Eventos, Listas y RRPP, Mensajes, Taquilla, Puerta, Indicadores, Avisos, Agenda y Asistente) llevan la clave,
 * el icono y el orden del prototipo (pendiente de las fichas). «Horario», «Políticas de pago» y «Para desarrolladores» van
 * dentro de «Negocio» (G130).
 */
const ENTRADAS: EntradaSimulada[] = [
    menu('analitica', 'inicio', 'Inicio', 'operations', 'home', 10, 'analitica.panel.ver', 'base'),
    menu('reservas', 'reservas', 'Reservas', 'operations', 'bookings', 20, 'reservas.reservas.ver', 'reservas'),
    menu('citas', 'agenda', 'Agenda', 'operations', 'agenda', 25, 'citas.agenda.ver', 'citas'),
    menu('reservas', 'sala', 'Sala', 'operations', 'floor', 30, 'sala.operativa.ver', 'plano_sala'),
    menu('clientes', 'clientes', 'Clientes', 'operations', 'customers', 40, 'clientes.fichas.ver', 'base'),
    menu('eventos-entradas', 'entradas', 'Eventos', 'events', 'events', 10, 'entradas.eventos.ver', 'entradas_eventos'),
    menu('listas-rrpp', 'listas', 'Listas y RRPP', 'events', 'guest_lists', 20, 'listas.listas.ver', 'listas_rrpp'),
    menu('eventos-entradas', 'pedidos', 'Entradas', 'events', 'tickets', 30, 'entradas.pedidos.ver', 'entradas_eventos'),
    menu('canales', 'buzon', 'Buzón', 'communication', 'inbox', 10, 'canales.buzon.atender', 'base', 3),
    menu('campanas', 'campanas', 'Campañas', 'communication', 'campaigns', 20, 'campanas.campanas.ver', 'mensajes_campanas'),
    menu('entregas', 'mensajes', 'Mensajes', 'communication', 'messages', 30, 'entregas.registro.ver', 'base'),
    menu('eventos-entradas', 'taquilla', 'Taquilla', 'access', 'box_office', 10, 'entradas.taquilla.vender', 'entradas_eventos'),
    menu('pases-acceso', 'escaner', 'Escáner', 'access', 'scanner', 20, 'pases.puerta.validar', 'escaner_tablet'),
    menu('pases-acceso', 'puerta', 'Puerta', 'access', 'door', 30, 'pases.escaneos.ver', 'escaner_tablet'),
    menu('analitica', 'indicadores', 'Indicadores', 'tracking', 'indicators', 10, 'analitica.operativos.ver', 'base'),
    menu('entregas', 'avisos', 'Avisos', 'tracking', 'notices', 20, 'entregas.avisos.recibir', 'base'),
    menu('negocios', 'negocio', 'Negocio', 'settings', 'business', 10, 'negocio.ajustes.editar', null),
    menu('reservas', 'recursos', 'Recursos', 'settings', 'resources', 30, 'reservas.configuracion.ver', 'reservas'),
    menu('pagos', 'pagos', 'Pagos', 'settings', 'payments', 40, 'pagos.cuenta.ver', 'base'),
    menu('reservas', 'plano', 'Plano', 'settings', 'floor_plan', 50, 'sala.plano.ver', 'plano_sala'),
    menu('canales', 'canales', 'Canales', 'settings', 'channels', 60, ['canales.canales.gestionar', 'canales.salud.ver'], 'base'),
    menu('asistente', 'asistente', 'Asistente', 'settings', 'assistant', 65, 'asistente.configuracion.editar', 'asistente_ia'),
    menu('web', 'web', 'Web pública', 'settings', 'website', 80, 'web.sitio.editar', 'web_publica'),
    dentro('disponibilidad', 'horario', 'negocio', 'section', 'Horario', 'schedule', 10, 'disponibilidad.horarios.ver', 'base'),
    dentro('pagos', 'politicas-de-pago', 'negocio', 'card', 'Políticas de pago', 'payments', 20, 'pagos.politicas.gestionar', 'base'),
    dentro('web', 'desarrolladores', 'negocio', 'card', 'Para desarrolladores', 'developers', 30, 'web.widgets.gestionar', 'widget'),
    perfil('identidad', 'cuenta', '/cuenta', 'Mi cuenta', 'account', 10, null),
    perfil('negocios', 'puesta', '/puesta-en-marcha', 'Puesta en marcha', 'setup', 20, 'negocio.ajustes.editar'),
    perfil('negocios', 'equipo', '/equipo', 'Equipo y roles', 'team', 30, 'equipo.miembros.ver'),
    perfil('planes', 'plan', '/plan', 'Plan y facturación', 'billing', 40, 'plan.ver'),
    perfil('audit', 'actividad', '/actividad', 'Actividad', 'activity', 50, 'actividad.ver'),
];

/** Categorías en el orden de `NavigationGroup` (G128): el orden en que el servidor manda las entradas. */
const CATEGORIAS: readonly GrupoNavegacion[] = ['operations', 'events', 'communication', 'access', 'tracking', 'settings'];

/**
 * Bloques contratados en el prototipo: Web pública no lo está en los negocios de reservas, así que la API solo se la da
 * al Propietario, con `not_contracted` y su bloque, para el «+» (G120). Peluquería Lola es de citas.
 */
const BLOQUES_RESERVAS = ['base', 'reservas', 'plano_sala', 'entradas_eventos', 'escaner_tablet', 'listas_rrpp', 'mensajes_campanas', 'asistente_ia', 'widget'];
const BLOQUES_CITAS = ['base', 'citas', 'mensajes_campanas'];

function bloquesDe(negocio: string): string[] {
    return negocio === NEGOCIOS.lola ? BLOQUES_CITAS : BLOQUES_RESERVAS;
}

/**
 * Bloques de otro sector, que el prototipo no ofrece ni con «+» (Caseta Embrujo no ve «Agenda»; Peluquería Lola no ve
 * Reservas, Sala ni Eventos). G120 no distingue sectores: queda pendiente del catálogo (P0-7). Solo en la simulación.
 */
function deOtroSector(negocio: string, bloque: string | null): boolean {
    return negocio === NEGOCIOS.lola ? ['reservas', 'plano_sala', 'entradas_eventos', 'escaner_tablet', 'listas_rrpp'].includes(bloque ?? '') : bloque === 'citas';
}

/**
 * Permisos efectivos que daría el servidor a la cuenta de la demo en cada negocio (los que cuentan para el menú y los
 * botones del Paso 0), calculados allí con los `default_roles` y `own_roles` del contrato: Propietario en Caseta
 * Embrujo y en Peluquería Lola, Administrador en Sala Andén. Los del Encargado sirven para las pruebas de otros roles.
 */
export const PERMISOS_ENCARGADO: PermisoEfectivo[] = [
    { code: 'actividad.ver', scope: 'own' },
    { code: 'analitica.operativos.ver', scope: 'all' },
    { code: 'analitica.panel.ver', scope: 'all' },
    { code: 'campanas.campanas.ver', scope: 'all' },
    { code: 'canales.buzon.atender', scope: 'all' },
    { code: 'canales.salud.ver', scope: 'all' },
    { code: 'citas.agenda.ver', scope: 'all' },
    { code: 'clientes.fichas.ver', scope: 'all' },
    { code: 'entradas.eventos.ver', scope: 'all' },
    { code: 'entradas.pedidos.ver', scope: 'all' },
    { code: 'entradas.taquilla.vender', scope: 'all' },
    { code: 'entregas.avisos.recibir', scope: 'all' },
    { code: 'entregas.registro.ver', scope: 'all' },
    { code: 'equipo.miembros.ver', scope: 'all' },
    { code: 'listas.listas.ver', scope: 'all' },
    { code: 'pases.escaneos.ver', scope: 'all' },
    { code: 'pases.puerta.validar', scope: 'all' },
    { code: 'reservas.configuracion.ver', scope: 'all' },
    { code: 'reservas.reservas.ver', scope: 'all' },
    { code: 'sala.operativa.ver', scope: 'all' },
    { code: 'sala.plano.ver', scope: 'all' },
];
const PERMISOS_ADMINISTRADOR: PermisoEfectivo[] = [
    ...PERMISOS_ENCARGADO.map((p) => ({ ...p, scope: 'all' as const })),
    ...[
        'asistente.configuracion.editar', 'asistente.configuracion.publicar', 'canales.canales.gestionar', 'disponibilidad.horarios.ver', 'equipo.miembros.gestionar', 'equipo.tablets.gestionar', 'negocio.ajustes.editar', 'negocio.ver',
        'pagos.cuenta.ver', 'pagos.politicas.gestionar', 'plan.ver', 'web.sitio.editar', 'web.widgets.gestionar',
    ].map((code) => ({ code, scope: 'all' as const })),
].sort((a, b) => a.code.localeCompare(b.code));
const PERMISOS_PROPIETARIO: PermisoEfectivo[] = [...PERMISOS_ADMINISTRADOR, { code: 'plan.contratar', scope: 'all' as const }].sort((a, b) => a.code.localeCompare(b.code));

const esPropietario = (negocio: string) => negocio === NEGOCIOS.embrujo || negocio === NEGOCIOS.lola;

export function permisosDelNegocio(negocio: string): PermisoEfectivo[] {
    return esPropietario(negocio) ? PERMISOS_PROPIETARIO : PERMISOS_ADMINISTRADOR;
}

/** Preferencias vacías (`NavigationPreferences` sin nada guardado). */
export const SIN_PREFERENCIAS: PreferenciasMenu = { group_order: null, in_more: null, mobile_bar: null, home_shortcuts: null, dismissed_notices: null, updated_at: null };

/**
 * Accesos de Inicio que declararían los módulos (G129), como «Tus accesos» del prototipo (captura 0.1). Sin `badge`:
 * `NavigationShortcut` no lo trae (la cifra de «Reservas de hoy» del prototipo queda pendiente del diseño de Inicio).
 */
const ACCESOS: { acceso: Omit<AccesoDeInicio, 'not_contracted'>; permiso: string; bloque: string }[] = [
    { acceso: { module: 'reservas', key: 'nueva_reserva', label: 'Nueva reserva', path: '/reservas?nueva=1', icon: 'add', order: 10, primary: true, kind: 'shortcut' }, permiso: 'reservas.reservas.ver', bloque: 'reservas' },
    { acceso: { module: 'citas', key: 'nueva_cita', label: 'Nueva cita', path: '/agenda?nueva=1', icon: 'add', order: 10, primary: true, kind: 'shortcut' }, permiso: 'citas.agenda.ver', bloque: 'citas' },
    { acceso: { module: 'reservas', key: 'reservas_hoy', label: 'Reservas de hoy', path: '/reservas', icon: 'bookings', order: 20, primary: false, kind: 'shortcut' }, permiso: 'reservas.reservas.ver', bloque: 'reservas' },
    { acceso: { module: 'reservas', key: 'sala', label: 'Sala', path: '/sala', icon: 'floor', order: 30, primary: false, kind: 'shortcut' }, permiso: 'sala.operativa.ver', bloque: 'plano_sala' },
    { acceso: { module: 'pases-acceso', key: 'escaner', label: 'Escáner', path: '/escaner', icon: 'scanner', order: 40, primary: false, kind: 'shortcut' }, permiso: 'pases.puerta.validar', bloque: 'escaner_tablet' },
    { acceso: { module: 'eventos-entradas', key: 'eventos', label: 'Eventos', path: '/entradas', icon: 'events', order: 50, primary: false, kind: 'shortcut' }, permiso: 'entradas.eventos.ver', bloque: 'entradas_eventos' },
    { acceso: { module: 'canales', key: 'buzon', label: 'Buzón', path: '/buzon', icon: 'inbox', order: 60, primary: false, kind: 'shortcut' }, permiso: 'canales.buzon.atender', bloque: 'base' },
    { acceso: { module: 'clientes', key: 'clientes', label: 'Clientes', path: '/clientes', icon: 'customers', order: 70, primary: false, kind: 'shortcut' }, permiso: 'clientes.fichas.ver', bloque: 'base' },
];

/**
 * getMyNavigation simulado: lo que haría el servidor (permiso, bloque y modo), sin listas en el panel. Lo que el rol no
 * puede ver no llega; lo no contratado, solo al Propietario (G120); `read_only` solo por el modo (G114). Por categorías
 * (G128): una sin entradas no llega y la que tiene una sola va al final de Operativa. Una entrada dentro de otra solo
 * llega con su madre (G130). Aplica las preferencias guardadas (G129) y trae el acceso de plataforma abierto (G137).
 * Se le pueden pasar otros permisos para probar otros roles.
 */
export function navegacion(
    negocio: string,
    soloLectura: boolean,
    permisos: PermisoEfectivo[] = permisosDelNegocio(negocio),
    preferencias: PreferenciasMenu = SIN_PREFERENCIAS,
    acceso: AccesoEnElMenu | null = null,
): Navegacion {
    const tiene = new Set(permisos.map((p) => p.code));
    const propietario = esPropietario(negocio) && permisos === permisosDelNegocio(negocio) && acceso?.viewer !== 'platform';
    const contratados = bloquesDe(negocio);
    const llegan = ENTRADAS.filter((e) => (e.permiso === null || [e.permiso].flat().some((p) => tiene.has(p))) && !deOtroSector(negocio, e.bloque))
        .map((e) => ({ e, sinContratar: e.bloque !== null && !contratados.includes(e.bloque) }))
        .filter(({ sinContratar }) => !sinContratar || propietario)
        .map(({ e, sinContratar }): EntradaNavegacion => ({
            ...e.item,
            read_only: soloLectura,
            in_more: false,
            entitlement: e.bloque,
            not_contracted: sinContratar,
        }));
    const claves = new Set(llegan.map((i) => i.key));
    const delMenu = llegan.filter((i) => i.area === 'menu' && i.parent_key == null);
    const sueltas: EntradaNavegacion[] = [];
    const porCategoria = new Map(CATEGORIAS.map((c) => [c, delMenu.filter((i) => i.group === c)]));
    for (const c of CATEGORIAS.slice(1)) {
        const suyas = porCategoria.get(c) ?? [];
        if (suyas.length === 1) {
            sueltas.push(...suyas.map((i) => ({ ...i, group: 'operations' as const })));
            porCategoria.set(c, []);
        }
    }
    porCategoria.set('operations', [...(porCategoria.get('operations') ?? []), ...sueltas]);
    const enMas = (preferencias.in_more ?? []).filter((k) => k !== 'inicio');
    const ordenado = CATEGORIAS.flatMap((c) => {
        const orden = preferencias.group_order?.[c] ?? [];
        const posicion = (k: string) => (orden.includes(k) ? orden.indexOf(k) : Number.MAX_SAFE_INTEGER);
        return [...(porCategoria.get(c) ?? [])]
            .map((i, n) => ({ i, n }))
            .sort((a, b) => posicion(a.i.key) - posicion(b.i.key) || a.n - b.n)
            .map(({ i }) => ({ ...i, in_more: enMas.includes(i.key) }));
    });
    const anidadas = llegan.filter((i) => i.area === 'menu' && i.parent_key != null && claves.has(i.parent_key));
    const items = [...ordenado, ...anidadas, ...llegan.filter((i) => i.area === 'profile')];

    const delMenuClaves = ordenado.map((i) => i.key);
    const guardada = (preferencias.mobile_bar ?? []).filter((k) => delMenuClaves.includes(k));
    const mobile_bar = guardada.length > 0 ? guardada.slice(0, 4) : ordenado.filter((i) => i.group === 'operations' && !i.not_contracted).map((i) => i.key).slice(0, 4);
    // Sin el bloque, solo al Propietario y con «+» (G963, como el menú).
    const shortcuts = ACCESOS.filter((a) => tiene.has(a.permiso) && !deOtroSector(negocio, a.bloque) && (contratados.includes(a.bloque) || propietario)).map(
        (a): AccesoDeInicio => ({ ...a.acceso, entitlement: a.bloque, not_contracted: !contratados.includes(a.bloque) }),
    );
    const guardados = (preferencias.home_shortcuts ?? []).filter((k) => shortcuts.some((a) => a.key === k && !a.not_contracted));
    const home_shortcuts = guardados.length > 0 ? guardados : shortcuts.filter((a) => !a.not_contracted).sort((a, b) => a.order - b.order).map((a) => a.key).slice(0, 6);
    return {
        mode: soloLectura ? 'read_only' : 'operational',
        items,
        mobile_bar,
        shortcuts,
        home_shortcuts,
        dismissed_notices: preferencias.dismissed_notices ?? [],
        platform_access: acceso,
        link_targets: [],
        permissions: permisos,
    };
}

/**
 * Reglas de `updateMyNavigationPreferences` (G129) en la simulación: el orden es dentro de cada categoría, Inicio no se
 * quita, la barra tiene de 1 a 4 entradas. Devuelve el campo que falla, o null.
 */
export function preferenciasNoValidas(negocio: string, cambios: PreferenciasMenu): string | null {
    const nav = navegacion(negocio, false);
    const grupoDe = new Map(nav.items.map((i) => [i.key, i.group]));
    for (const [grupo, claves] of Object.entries(cambios.group_order ?? {})) {
        if (claves.some((k) => grupoDe.has(k) && grupoDe.get(k) !== grupo)) {
            return 'group_order';
        }
    }
    if ((cambios.in_more ?? []).includes('inicio')) {
        return 'in_more';
    }
    if (cambios.mobile_bar != null && (cambios.mobile_bar.length < 1 || cambios.mobile_bar.length > 4)) {
        return 'mobile_bar';
    }
    if (cambios.home_shortcuts != null && cambios.home_shortcuts.length > 6) {
        return 'home_shortcuts';
    }
    return null;
}

/** Une lo guardado con los cambios: solo cambian los campos que llegan; `null` vuelve a lo de por defecto. */
export function unirPreferencias(guardadas: PreferenciasMenu, cambios: PreferenciasMenu, ahora: string): PreferenciasMenu {
    return { ...guardadas, ...cambios, updated_at: ahora };
}

const enDias = (ahora: Date, dias: number) => new Date(ahora.getTime() + dias * 86_400_000).toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * Fechas de la suscripción del prototipo, relativas a hoy: 9 días de prueba, 14 de gracia del impago y la baja a 18 días
 * (en el prototipo, «hoy» es el 14 de octubre: 23 de octubre, 28 de octubre y 1 de noviembre).
 */
function fechas(estado: EstadoSuscripcion, ahora: Date) {
    return {
        trial_ends_at: estado === 'trialing' ? enDias(ahora, 9) : null,
        grace_ends_at: estado === 'past_due' ? enDias(ahora, 14) : null,
        cancel_at: estado === 'cancel_scheduled' ? enDias(ahora, 18) : null,
    };
}

/**
 * listEntitlements simulado. Pendiente P0-9: además de lo del contrato lleva `trial_ends_at` y `grace_ends_at` (la
 * recomendación de P0-9 es añadirlos a `listEntitlements` para que quien no tiene `plan.ver` vea los días de la franja).
 */
export function bloques(negocio: string, estado: EstadoSuscripcion, ahora: Date, soloLectura = modoDe(estado) === 'read_only'): Bloques & { trial_ends_at: string | null; grace_ends_at: string | null } {
    const { trial_ends_at, grace_ends_at } = fechas(estado, ahora);
    return {
        mode: soloLectura ? 'read_only' : 'operational',
        status: estado,
        entitlements: bloquesDe(negocio).map((product_code) => ({ product_code, quantity: 1, source: 'subscription' as const, since: '2026-09-01T00:00:00Z' })),
        trial_ends_at,
        grace_ends_at,
    };
}

/** getSubscription simulado (`plan.ver`). */
export function suscripcion(negocio: string, estado: EstadoSuscripcion, ahora: Date): Suscripcion {
    const b = bloques(negocio, estado, ahora);
    return {
        id: `sub_${negocio.slice(4)}`,
        status: estado,
        mode: b.mode,
        ...fechas(estado, ahora),
        current_period_start: '2026-10-01T00:00:00Z',
        current_period_end: '2026-11-01T00:00:00Z',
        pause: estado === 'paused' ? { started_at: '2026-10-01T00:00:00Z', expected_return_on: '2027-03-01' } : null,
        coupon_code: null,
        items: b.entitlements,
    };
}

/** getUsage simulado: conversaciones del asistente al 60 %, con `?simular=cupo` al 90 % y con `cupo100`, agotadas (G777). */
export function uso(cupo: boolean, agotado = false): { data: Contador[] } {
    return {
        data: [{ concept: 'conversations', period_start: '2026-10-01', period_end: '2026-10-31', used: agotado ? 500 : cupo ? 450 : 300, included: 500, extra: 0, warned_90_at: null, exhausted_at: null }],
    };
}

/**
 * Acceso de plataforma abierto que el servidor manda en `platform_access` de getMyNavigation (G137), como el prototipo
 * (0.5): Soporte en solo lectura o suplantando a Uri Ramírez, con 58 minutos por delante, para la persona de Reservaria
 * (`platform`); o el que ve el negocio (`business`, franja negra), sin el motivo.
 */
export function accesoSimulado(tipo: 'soporte' | 'suplantacion' | 'negocio', ahora: Date): AccesoEnElMenu {
    const suplanta = tipo === 'suplantacion';
    return {
        id: 'pac_01JA8Z6Q5V3K9R2M7T4N8P1AC0',
        viewer: tipo === 'negocio' ? 'business' : 'platform',
        mode: suplanta ? 'impersonation' : 'read',
        reason: tipo === 'negocio' ? null : 'El cliente dice que no le llegan los recordatorios',
        impersonated_member_id: suplanta ? 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M01' : null,
        impersonated_member_name: suplanta ? 'Uri Ramírez' : null,
        write_grant_active: false,
        opened_at: enDias(ahora, -2 / 1440),
        expires_at: new Date(ahora.getTime() + 58 * 60_000 - 1000).toISOString().replace(/\.\d{3}Z$/, 'Z'),
    };
}

/** getBusiness simulado: los datos generales del negocio (el panel solo usa el nombre si quien mira no es miembro). */
export function negocioSimulado(negocio: string): Negocio {
    const [name, slug] = negocio === NEGOCIOS.lola ? ['Peluquería Lola', 'peluqueria-lola'] : negocio === NEGOCIOS.anden ? ['Sala Andén', 'sala-anden'] : ['Caseta Embrujo', 'embrujo'];
    return {
        id: negocio,
        name,
        sector: negocio === NEGOCIOS.lola ? 'salon' : 'nightclub',
        country: 'ES',
        timezone: 'Europe/Madrid',
        currency: 'EUR',
        locale: 'es-ES',
        slug,
        slug_changes_remaining: 3,
        owner_member_id: 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M01',
        pending_ownership_transfer: null,
        privacy_url: `https://reservaria.app/${slug}/privacidad`,
        status: 'active',
        created_at: '2026-09-01T09:00:00Z',
    };
}

/**
 * Las cinco listas pequeñas del módulo 1 con su total para la cifra de la pestaña (G139): Miembros 6, Invitaciones 2,
 * Roles 8 (cada uno con `member_count`), Tablets 3 y Facturas 2. Sus pantallas llegan con el módulo.
 */
export function listasPequenas(): { miembros: ListaMiembros; invitaciones: ListaInvitaciones; roles: ListaRoles; tablets: ListaTablets; facturas: ListaFacturas } {
    const personas = ['Uri Ramírez', 'Lucía Pérez', 'Marta Gil', 'Jorge Sanz', 'Ana Ruiz', 'Pablo Díaz'];
    const rolesDe = ['Propietario', 'Administrador', 'Encargado', 'Recepción', 'Recepción', 'Profesional'];
    const id = (prefijo: string, n: number) => `${prefijo}_01JA8Z6Q5V3K9R2M7T4N8P1X${String(n).padStart(2, '0')}`;
    const roles: [string, string, number][] = [
        ['administrator', 'Administrador', 1], ['manager', 'Encargado', 1], ['front_desk', 'Recepción', 2], ['professional', 'Profesional', 1],
        ['promoter', 'Relaciones públicas', 0], ['associate', 'Colaborador', 0], ['door', 'Puerta', 0], ['custom_barra', 'Barra', 0],
    ];
    return {
        miembros: {
            data: personas.map((display_name, n) => ({
                id: `mem_01JA8Z6Q5V3K9R2M7T4N8P1M${String(n + 1).padStart(2, '0')}`,
                account_id: id('acc', n),
                display_name,
                email: `${display_name.split(' ')[0]?.toLocaleLowerCase('es') ?? 'persona'}@embrujo.es`,
                status: 'active',
                is_owner: n === 0,
                roles: [{ id: id('rol', n), code: rolesDe[n] ?? '', name: rolesDe[n] ?? '' }],
                location_ids: [],
                joined_at: '2026-09-01T09:00:00Z',
            })),
            next_cursor: null,
            total_count: 6,
        },
        invitaciones: {
            data: ['sara@embrujo.es', 'leo@embrujo.es'].map((email, n) => ({ id: id('inv', n), email, roles: [{ id: id('rol', 2), code: 'front_desk', name: 'Recepción' }], status: 'sent' as const, expires_at: '2026-10-28T09:00:00Z', created_at: '2026-10-14T09:00:00Z' })),
            next_cursor: null,
            total_count: 2,
        },
        roles: {
            data: roles.map(([code, name, member_count], n) => ({ id: id('rol', n), kind: code.startsWith('custom_') ? ('custom' as const) : ('predefined' as const), code, name, permissions: [], member_count })),
            total_count: 8,
        },
        tablets: {
            data: ['Puerta principal', 'Barra', 'Terraza'].map((name, n) => ({ id: id('dev', n), name, location_id: null, status: 'active', paired_at: '2026-09-10T09:00:00Z', last_seen_at: null })),
            next_cursor: null,
            total_count: 3,
        },
        facturas: {
            data: ['2026-0009', '2026-0010'].map((number, n) => ({ id: id('inv', 10 + n), number, status: 'paid', total: { amount: 4900, currency: 'EUR' }, period_start: `2026-0${String(8 + n)}-01`, period_end: `2026-0${String(8 + n)}-30` })),
            next_cursor: null,
            total_count: 2,
        },
    };
}

/** Los avisos de la campana del prototipo (0.2): uno crítico y el resto pendientes, del más reciente al más antiguo. */
/** Segunda línea de cada aviso (`body`), sin datos personales (createNotice). */
const CUERPOS: Readonly<Record<number, string>> = {
    1: 'Hasta que lo vuelvas a conectar, los mensajes a tus clientes salen por email y nadie puede reservar por WhatsApp.',
    4: 'El asistente ha pasado al buzón una conversación de WhatsApp que no sabe resolver.',
    6: 'Quedan 4 reservas sin marcar si vinieron: 2 de ayer y 2 de hoy.',
};

/** Tipos de aviso que le llegan a la persona (`listMyNoticeTypes`, G297), para «Silenciados». */
export function tiposDeAviso(): { notice_type: string; module: string; title: string; description: string | null; severity: Aviso['severity']; audience: string; mutable: boolean; muted: boolean; muted_at: string | null }[] {
    const tipo = (notice_type: string, title: string, severity: Aviso['severity'], muted = false) => ({ notice_type, module: notice_type.split('.')[0] ?? '', title, description: null, severity, audience: 'roles', mutable: severity !== 'critical', muted, muted_at: muted ? '2026-10-10T10:00:00Z' : null });
    return [
        tipo('canales.canal_caido', 'Canal desconectado', 'critical'),
        tipo('canales.pide_persona', 'Conversación para una persona', 'warning'),
        tipo('reservas.por_completar', 'Reservas por completar', 'warning'),
        tipo('asistente.cupo_casi_agotado', 'Asistente casi sin cupo', 'info', true),
    ];
}

export function avisos(ahora: Date): Aviso[] {
    const hace = (minutos: number) => new Date(ahora.getTime() - minutos * 60_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
    const aviso = (n: number, notice_type: string, severity: Aviso['severity'], title: string, minutos: number, link: Aviso['link']): Aviso => ({
        id: `ntc_01JA8Z6Q5V3K9R2M7T4N8P1A0${String(n)}`,
        notice_type,
        severity,
        title,
        body: CUERPOS[n] ?? null,
        link,
        emailed: severity === 'critical',
        status: 'open',
        occurrences: 1,
        last_occurred_at: hace(minutos),
        created_at: hace(minutos),
        state: 'unread',
        read_at: null,
        archived_at: null,
    });
    return [
        aviso(1, 'canales.canal_caido', 'critical', 'WhatsApp se ha desconectado', 120, { type: 'canal', id: 'chn_01JA8Z6Q5V3K9R2M7T4N8P1WA0' }),
        aviso(2, 'reservas.pendiente_completar', 'warning', '1 reserva sin mesa', 0, { type: 'reserva', id: 'res_01JA8Z6Q5V3K9R2M7T4N8P1R01' }),
        aviso(3, 'pagos.reembolso_externo', 'warning', 'Reembolso hecho en Stripe', 20, null),
        aviso(4, 'canales.pide_persona', 'warning', 'Una conversación pide una persona', 4, null),
        aviso(5, 'campanas.pausada_sin_paquete', 'warning', 'Campaña pausada: sin paquete Marketing', 120, null),
        aviso(6, 'reservas.por_completar', 'warning', '4 reservas por completar', 60, null),
        aviso(7, 'reservas.cierre_sin_recolocar', 'warning', '2 clientes por llamar tras un cierre', 30, null),
        aviso(8, 'asistente.cupo_casi_agotado', 'info', 'Asistente al 90 %', 60 * 26, null),
    ];
}
